import { useEffect, useState } from 'react';
import { Download, RefreshCw, X } from 'lucide-react';
import { checkForUpdate, relaunchApp, type UpdateAvailable } from '@/lib/updater';
import { isDesktop } from '@/lib/desktop';
import clsx from 'clsx';

/**
 * Yeni MSI çıkınca uygulamanın üstünde görünen ince banner.
 * - İlk açılışta 3sn sonra kontrol eder
 * - Sonra her saatte bir tekrar kontrol
 * - Yeni sürüm varsa: indir + yeniden başlat butonları
 */
export function UpdateBanner() {
  const [update, setUpdate] = useState<UpdateAvailable | null>(null);
  const [progress, setProgress] = useState<{ bytes: number; total?: number } | null>(null);
  const [installing, setInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isDesktop) return;
    let cancel = false;
    const poll = async () => {
      const r = await checkForUpdate();
      if (!cancel && r.available) setUpdate(r);
    };
    const t1 = setTimeout(poll, 3_000);
    const t2 = setInterval(poll, 60 * 60 * 1_000); // hourly
    return () => { cancel = true; clearTimeout(t1); clearInterval(t2); };
  }, []);

  if (!update || dismissed) return null;

  const pct = progress?.total ? Math.round((progress.bytes / progress.total) * 100) : null;

  const install = async () => {
    setInstalling(true);
    try {
      await update.downloadAndInstall((bytes, total) => {
        setProgress((p) => ({ bytes: (p?.bytes ?? 0) + bytes, total }));
      });
      // Auto-restart picks up the new binary
      await relaunchApp();
    } catch (e) {
      console.warn('[updater] install failed', e);
      setInstalling(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1.5 text-[12px] flex items-center justify-between gap-2 shadow-md">
      <div className="flex items-center gap-2 min-w-0">
        <Download size={13} className="flex-shrink-0" />
        <span className="truncate">
          <span className="font-semibold">Yeni sürüm {update.version}</span>
          <span className="text-blue-100 ml-1.5">({update.currentVersion} kullanıyorsun)</span>
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {installing && pct != null && (
          <span className="text-[11px] font-mono text-blue-100">%{pct}</span>
        )}
        <button
          onClick={install}
          disabled={installing}
          className={clsx(
            'flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-medium transition',
            installing
              ? 'bg-surface/10 text-blue-100 cursor-wait'
              : 'bg-surface text-blue-700 hover:bg-blue-50 active:scale-95',
          )}
        >
          {installing
            ? <><RefreshCw size={11} className="animate-spin" /> İndiriliyor…</>
            : <><Download size={11} /> Şimdi yükle</>}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-blue-100 hover:text-white hover:bg-surface/10 rounded transition"
          title="Şimdilik gizle"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}
